/**
 * The words of the crisis help a critical safety banner offers (D2, 21 Oct
 * 2026) — the one place both clients name a line, so the web dialog and the
 * phone's sheet cannot word them apart. The lines are the server's (the
 * banner's `escalationAction.lines`); only their words are the client's.
 */
import { useTranslation } from '@tonnie/locale';
import type { CrisisLine, CrisisLineAvailability, CrisisLineId } from '@tonnie/types';
import { useCallback } from 'react';

export interface CrisisLineWords {
  /** The service, by name ("European emergency number"). */
  name: string;
  /** When it answers ("24 hours, every day"). */
  availability: string;
}

/** `(line) → its name and hours`, in the reader's language. */
export function useCrisisLineWords(): (line: CrisisLine) => CrisisLineWords {
  const { t } = useTranslation();

  const nameOf = useCallback(
    (id: CrisisLineId): string => {
      switch (id) {
        case 'emergency':
          return t('copilot.safety.help.lines.emergency');
        case 'sns24':
          return t('copilot.safety.help.lines.sns24');
        default: {
          const unreachable: never = id;
          return unreachable;
        }
      }
    },
    [t]
  );

  const availabilityOf = useCallback(
    (availability: CrisisLineAvailability): string => {
      switch (availability) {
        case 'always':
          return t('copilot.safety.help.availability.always');
        default: {
          const unreachable: never = availability;
          return unreachable;
        }
      }
    },
    [t]
  );

  return useCallback(
    (line: CrisisLine) => ({
      name: nameOf(line.id),
      availability: availabilityOf(line.availability),
    }),
    [nameOf, availabilityOf]
  );
}

/** The `tel:` address a line is dialled at. */
export function crisisLineHref(line: CrisisLine): string {
  return `tel:${line.dial}`;
}
